import { useState } from 'react';
import SEO from '../../components/base/SEO';

export default function ContactPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact Proform Chimney Systems',
    description: 'Get in touch with Proform Chimney Systems. Contact us for chimney system inquiries, quotes, and support.',
    url: `${siteUrl}/contact`,
    mainEntity: {
      '@type': 'LocalBusiness',
      name: 'Proform Chimney Systems',
      image: 'https://proformbaca.com/wp-content/uploads/2024/02/logoproform1.png',
      '@id': siteUrl,
      url: siteUrl,
      telephone: '+90-543-457-61-96',
      email: 'info@proformbaca.com',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Akçaburgaz Mah. 3028 Sk. No:1 İç Kapı No:27',
        addressLocality: 'Esenyurt',
        addressRegion: 'Istanbul',
        postalCode: '34522',
        addressCountry: 'TR'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 41.0082,
        longitude: 28.9784
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '09:00',
          closes: '18:00'
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: 'Saturday',
          opens: '09:00',
          closes: '13:00'
        }
      ],
      sameAs: [
        'https://www.linkedin.com/company/proform-chimney-system/',
        'https://www.instagram.com/proformbaca/'
      ]
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.message.length > 500) {
      alert('Message cannot exceed 500 characters');
      return;
    }

    setSubmitStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6kiiug5dsf7sr1n5gdg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus('idle'), 5000);
      }
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <>
      <SEO
        title="Contact Proform Chimney Systems - Get Expert Chimney Solutions in Turkey"
        description="Contact Proform Chimney Systems in Istanbul, Turkey. Get expert advice on chimney systems, request quotes, and schedule consultations. Phone: +90 543 457 61 96"
        keywords="contact Proform, chimney quote Turkey, chimney consultation Istanbul, chimney expert Turkey, chimney installation contact"
        canonical={`${siteUrl}/contact`}
        schema={schema}
      />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-20">
        {/* Hero Section */}
        <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://public.readdy.ai/ai/img_res/4d2a3d7ba00c5f4b7fcadd7438a9411d.jpg"
              alt="Contact Hero"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a8a]/90 via-[#1e3a8a]/80 to-orange-600/70"></div>
          </div>
          
          <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Get in Touch</h1>
            <p className="text-xl md:text-2xl leading-relaxed">
              Have questions about our chimney systems? Our expert team is ready to help you find the perfect solution.
            </p>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Contact Form - 2 columns */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-3xl shadow-2xl p-10 border border-gray-100">
                  <div className="mb-8">
                    <h2 className="text-4xl font-bold text-gray-900 mb-3">Send us a Message</h2>
                    <p className="text-gray-600 text-lg">Fill out the form below and we'll get back to you within 24 hours.</p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6" id="contact-form" data-readdy-form>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-gray-800 font-semibold mb-2 text-sm">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 text-sm"
                          placeholder="John Doe"
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-gray-800 font-semibold mb-2 text-sm">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 text-sm"
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="phone" className="block text-gray-800 font-semibold mb-2 text-sm">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 text-sm"
                          placeholder="+90 XXX XXX XX XX"
                        />
                      </div>

                      <div>
                        <label htmlFor="subject" className="block text-gray-800 font-semibold mb-2 text-sm">
                          Subject *
                        </label>
                        <input
                          type="text"
                          id="subject"
                          name="subject"
                          required
                          value={formData.subject}
                          onChange={handleChange}
                          className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 text-sm"
                          placeholder="How can we help?"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-gray-800 font-semibold mb-2 text-sm">
                        Your Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        rows={6}
                        maxLength={500}
                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none transition-all duration-300 text-sm"
                        placeholder="Tell us about your project or inquiry..."
                      ></textarea>
                      <div className="flex justify-between items-center mt-2">
                        <p className="text-sm text-gray-500">
                          {formData.message.length}/500 characters
                        </p>
                        {formData.message.length > 500 && (
                          <p className="text-sm text-red-500 font-semibold">
                            Message exceeds maximum length
                          </p>
                        )}
                      </div>
                    </div>

                    {submitStatus === 'success' && (
                      <div className="bg-green-50 border-2 border-green-500 text-green-700 px-6 py-4 rounded-xl">
                        <div className="flex items-center space-x-3">
                          <i className="ri-checkbox-circle-fill text-2xl"></i>
                          <span className="font-semibold">Message sent successfully! We'll get back to you soon.</span>
                        </div>
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="bg-red-50 border-2 border-red-500 text-red-700 px-6 py-4 rounded-xl">
                        <div className="flex items-center space-x-3">
                          <i className="ri-error-warning-fill text-2xl"></i>
                          <span className="font-semibold">Failed to send message. Please try again.</span>
                        </div>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitStatus === 'submitting' || formData.message.length > 500}
                      className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-5 rounded-xl text-lg font-bold hover:from-orange-600 hover:to-orange-700 transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center space-x-3"
                    >
                      {submitStatus === 'submitting' ? (
                        <>
                          <i className="ri-loader-4-line animate-spin text-2xl"></i>
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-fill text-xl"></i>
                          <span>Send Message</span>
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>

              {/* Contact Information - 1 column */}
              <div className="space-y-6">
                {/* Quick Contact Cards */}
                <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-3xl shadow-2xl p-8 text-white">
                  <h3 className="text-2xl font-bold mb-6">Quick Contact</h3>
                  
                  <div className="space-y-5">
                    <a href="tel:+905434576196" className="flex items-center space-x-4 p-4 bg-white/10 rounded-xl hover:bg-white/20 transition-all duration-300 cursor-pointer group">
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                        <i className="ri-phone-fill text-2xl"></i>
                      </div>
                      <div>
                        <p className="text-sm text-white/80 mb-1">Call Us</p>
                        <p className="font-bold text-lg">+90 543 457 61 96</p>
                      </div>
                    </a>

                    <a href="mailto:info@proformbaca.com" className="flex items-center space-x-4 p-4 bg-white/10 rounded-xl hover:bg-white/20 transition-all duration-300 cursor-pointer group">
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                        <i className="ri-mail-fill text-2xl"></i>
                      </div>
                      <div>
                        <p className="text-sm text-white/80 mb-1">Email Us</p>
                        <p className="font-bold">info@proformbaca.com</p>
                      </div>
                    </a>

                    <a href="https://wa.me/905434576196" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-4 p-4 bg-white/10 rounded-xl hover:bg-white/20 transition-all duration-300 cursor-pointer group">
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                        <i className="ri-whatsapp-fill text-2xl"></i>
                      </div>
                      <div>
                        <p className="text-sm text-white/80 mb-1">WhatsApp</p>
                        <p className="font-bold">Chat with us</p>
                      </div>
                    </a>
                  </div>
                </div>

                {/* Address Card */}
                <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-fill text-2xl text-orange-600"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-xl text-gray-900 mb-2">Visit Us</h3>
                      <p className="text-gray-600 leading-relaxed">
                        Akçaburgaz Mah. 3028 Sk. No:1<br />
                        İç Kapı No:27<br />
                        Esenyurt / Istanbul<br />
                        <strong className="text-gray-900">TURKEY</strong>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Business Hours Card */}
                <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#1e3a8a]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-fill text-2xl text-[#1e3a8a]"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-xl text-gray-900 mb-3">Business Hours</h3>
                      <div className="space-y-2 text-gray-600">
                        <p><strong className="text-gray-900">Mon - Fri:</strong> 9:00 AM - 6:00 PM</p>
                        <p><strong className="text-gray-900">Saturday:</strong> 9:00 AM - 1:00 PM</p>
                        <p><strong className="text-gray-900">Sunday:</strong> Closed</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Social Media Card */}
                <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Follow Us</h3>
                  <div className="flex space-x-4">
                    <a
                      href="https://www.linkedin.com/company/proform-chimney-system/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center text-white hover:scale-110 hover:shadow-xl transition-all duration-300 cursor-pointer"
                    >
                      <i className="ri-linkedin-fill text-2xl"></i>
                    </a>
                    <a
                      href="https://www.instagram.com/proformbaca/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center text-white hover:scale-110 hover:shadow-xl transition-all duration-300 cursor-pointer"
                    >
                      <i className="ri-instagram-fill text-2xl"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Map Section */}
            <div className="mt-16">
              <div className="bg-white rounded-3xl shadow-2xl p-4 border border-gray-100 overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3011.2!2d28.9784!3d41.0082!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDHCsDAwJzI5LjUiTiAyOMKwNTgnNDIuMiJF!5e0!3m2!1sen!2str!4v1234567890"
                  width="100%"
                  height="450"
                  style={{ border: 0, borderRadius: '1.5rem' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Proform Chimney Systems Location"
                ></iframe>
              </div>
            </div>

            {/* Bank Information Section */}
            <div className="mt-16">
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl shadow-2xl p-10 text-white">
                <div className="flex items-center space-x-4 mb-8">
                  <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
                    <i className="ri-bank-fill text-3xl"></i>
                  </div>
                  <h3 className="text-3xl font-bold">Bank Information</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                    <p className="text-orange-400 font-bold text-lg mb-4">Enpara Bank A.Ş.</p>
                    <div className="space-y-2">
                      <p className="text-white/70 text-sm">Account IBAN (TL)</p>
                      <p className="font-mono text-lg font-semibold">TR26 0015 7000 0000 0098 5792 95</p>
                    </div>
                  </div>
                  
                  <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                    <p className="text-orange-400 font-bold text-lg mb-4">Enpara Bank A.Ş.</p>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/70 text-sm">Account IBAN (EUR)</p>
                        <p className="font-mono text-lg font-semibold">TR62 0015 7000 0000 0098 5795 20</p>
                      </div>
                      <div>
                        <p className="text-white/70 text-sm">Swift Code</p>
                        <p className="font-mono text-lg font-semibold">ENASTRISXXX</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}